import { ITripService } from '@/features/trips/ITripService';
import { injectable, inject } from 'inversify-props';
import { MpcGetTripsResponseDto } from '@/features/trips/MpcGetTripsResponseDto';
import { PassingTime } from '@/features/trips/PassingTime';
import { TripUpdateRequest } from '@/features/trips/TripUpdateRequest';
import { Trip } from '@/features/trips/Trip';
import { ITripsApi, ITripsApiId } from '@/features/trips/ITripsApi';
import { PassingTimeUpdateRequest } from '@/features/trips/PassingTimeUpdateRequest';
import { TransportType } from '@/features/shared/models/TransportType';
import { BookingData, BookingNoShowPage, DeleteBookingNoShowRequest } from './BookingNoShowModels';
import { getLastKnownLocation } from '@/features/shared/lastKnownPosition';

@injectable()
export default class TripService implements ITripService {
    @inject(ITripsApiId)
    api!: ITripsApi;

    async getTrips(lineNumber: string, calendarDay?: string): Promise<MpcGetTripsResponseDto> {
        try {
            return await this.api.getTrips(lineNumber, calendarDay);
        } catch {
            return {
                lineNumber: '',
                lineName: '',
                calendarDay: '',
                token: '',
                transportType: TransportType.None,
                singleViewMode: false,
                trips: new Array<Trip>()
            };
        }
    }

    async createTrip(tripId: string, calendarDay: string): Promise<void> {
        try {
            await this.api.createTrip(tripId, calendarDay);
        } catch (error: any) {
            if (error.message.indexOf('404') > -1) {
                throw new Error('createTripFailed');
            } else {
                throw error;
            }
        }
    }

    async updateTrip(trip: Trip): Promise<any> {
        try {
            const data: TripUpdateRequest = {
                tripId: trip.tripId,
                operatingDay: trip.operatingDay,
                vesselName: trip.vesselName,
                timeStamp: new Date().getTime()
            };

            await this.api.updateTrip(data);
        } catch {
            /* empty */
        }
    }

    async updatePassingTime(trip: Trip, passingTime: PassingTime, timeStamp: number): Promise<any> {
        try {
            const position = getLastKnownLocation();
            const location = position ? { lat: position.lat, lng: position.lng } : null;

            const data: PassingTimeUpdateRequest = {
                tripId: trip.tripId,
                operatingDay: trip.operatingDay,
                passingTime,
                timeStamp: timeStamp,
                location: location,
                transportType: trip.transportType
            };

            await this.api.updatePassingTime(data);
        } catch {
            /* empty */
        }
    }

    async getBookingNoShowList(page: BookingNoShowPage, filters: any) {
        try {
            const res = await this.api.getBookingNoShows(page, filters);
            return res;
        } catch {
            /* empty */
        }
    }

    async registerBookingNoShow(bookingData: BookingData) {
        try {
            await this.api.registerBookingNoShow(bookingData);
        } catch {
            /* empty */
        }
    }

    async deleteBookingNoShow(request: DeleteBookingNoShowRequest) {
        try {
            await this.api.deleteBookingNoShow(request);
        } catch {
            /* empty */
        }
    }

    async completeTrip(tripId: string, operatingDay: string) {
        try {
            await this.api.completeTrip(tripId, operatingDay);
        } catch {
            /* empty */
        }
    }

    async cancelTrip(tripId: string, operatingDay: string) {
        try {
            await this.api.cancelTrip(tripId, operatingDay);
        } catch {
            /* empty */
        }
    }
}
